import React, { useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  CircularProgress,
  Divider,
  Button,
  useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useAppDispatch, useAppSelector, fetchCommentsByFeedId, loadMoreComments, clearComments } from '@/store';
import { CommentForm } from './CommentForm';
import { CommentItem } from './CommentItem';

interface CommentListProps {
  feedId: string;
  showComments: boolean;
}

export const CommentList: React.FC<CommentListProps> = ({ 
  feedId, 
  showComments
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { 
    currentFeedId, 
    comments, 
    pagination, 
    isLoading, 
    isLoadingMore 
  } = useAppSelector((state) => state.comments)
  console.log(useAppSelector((state) => state.comments))
  const isCurrentFeed = currentFeedId === feedId;
  const hasMorePages = pagination && pagination.current < pagination.pages;

  // Group comments by parent-child relationship
  const groupedComments = useMemo(() => {
    if (!comments.length) return [];
    
    // Separate parent comments and replies
    const parentComments = comments.filter(comment => !comment.parentCommentId);
    const replies = comments.filter(comment => comment.parentCommentId);
    
    // Group replies by parent ID
    const repliesByParent = replies.reduce((acc, reply) => {
      const parentId = reply.parentCommentId!;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(reply);
      return acc;
    }, {} as Record<string, typeof replies>);
    
    // Attach replies to parent comments
    return parentComments.map(parentComment => ({
      ...parentComment,
      replies: repliesByParent[parentComment._id] || []
    }));
  }, [comments]);

  const handleLoadMore = async () => {
    if (hasMorePages && pagination) {
      await dispatch(loadMoreComments({ 
        feedId, 
        params: { 
          page: pagination.current + 1, 
          limit: 10, 
          sort: '-createdAt', 
          includeReplies: true 
        } 
      }));
    }
  };

  const commentsContainerRef = useRef<HTMLDivElement>(null);

  // Always fetch fresh comments when opening comments or switching feeds
  React.useEffect(() => {
    if (showComments) {
      // Clear existing comments if switching to different feed
      if (currentFeedId && currentFeedId !== feedId) {
        dispatch(clearComments());
      }
      
      // Always fetch fresh comments when opening
      dispatch(fetchCommentsByFeedId({ 
        feedId, 
        params: { 
          page: 1, 
          limit: 10, 
          sort: '-createdAt', 
          includeReplies: true 
        } 
      }));
    }
  }, [showComments, feedId, dispatch]);

  // Scroll to top when comments open
  useEffect(() => {
    if (showComments && commentsContainerRef.current) {
      commentsContainerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }
  }, [showComments]);

  return (
    <Collapse in={showComments}>
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'grey.25' }}>
        
        {/* Initial Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Comments Section */}
        {!isLoading && (isCurrentFeed || (currentFeedId === feedId)) && (
          <Box ref={commentsContainerRef} sx={{ p: 2 }}>
            
            {/* Comment Form - Moved to top */}
            <Box sx={{ mb: 3 }}>
              <CommentForm 
                feedId={feedId}
                placeholder={groupedComments.length === 0 ? "Be the first to comment..." : "Write a comment..."}
              />
            </Box>
            
            {groupedComments.length > 0 && <Divider sx={{ mb: 2 }} />}
            
            {/* Comments List - Scrollable with max 4 comments */}
            {groupedComments.length > 0 ? (
              <Box 
                sx={{ 
                  maxHeight: '400px', // Height for roughly 4 comments
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
                {groupedComments.map((comment, index) => (
                  <Box key={comment._id}>
                    <CommentItem 
                      comment={comment} 
                      feedId={feedId}
                    />
                    {index < groupedComments.length - 1 && (
                      <Divider sx={{ my: 1, opacity: 0.3 }} />
                    )}
                  </Box>
                ))}
                
                {/* Load More Button */}
                {hasMorePages && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      startIcon={isLoadingMore ? <CircularProgress size={16} /> : <ExpandMore />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                      }}
                    >
                      {isLoadingMore ? 'Loading...' : `Load More`}
                      {/* {isLoadingMore ? 'Loading...' : `Load More (${pagination!.total - comments.length} remaining)`} */}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                align="center"
                sx={{ py: 3 }}
              >
                No comments yet. Be the first to comment!
              </Typography>
            )}
          </Box>
        )}

        {/* Loading different feed */}
        {!isLoading && !isCurrentFeed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Loading comments...
            </Typography>
          </Box>
        )}
        
      </Box>
    </Collapse>
  );
};